import { memo } from 'react';
import { CheckCheck } from 'lucide-react';

import { getByPath } from '@/utils/widgets';
import { WidgetData } from '@/components/ui/widget/widget.type';
import { BooleanView } from '@/components/ui/widget/views/boolean/boolean.view.type';
import { TextView } from '@/components/ui/widget/views/text/text.view.type';

type Props = {
  view: BooleanView;
  data: WidgetData;
};

const BooleanViewImpl = memo(({ view, data }: Props) => {
  const { path } = view;

  // if the path doesn't return a truthy value when applied to data, we don't show the check mark
  if (!getByPath(data, path)) {
    return null;
  }

  return (
    <div className="flex w-full items-center justify-center text-center">
      <CheckCheck size={24} className="text-muted-foreground" />
    </div>
  );
});

// sort defaults to text on path
const getSortConfig = (view: BooleanView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: BooleanView): TextView => ({
  type: 'text',
  path: view.path
});

// width defaults to 'small'
const getDefaultWidth = () => 'small' as const;

// stringifies data at path
const getStringifyFunction = (view: BooleanView) => {
  const { path } = view;
  if (path !== undefined)
    return (data: WidgetData) => (!!getByPath(data, path)).toString();
  return () => '';
};

export const booleanViewProvider = {
  component: BooleanViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
